import React from 'react';
import { 
  Clock, 
  User, 
  Edit, 
  CheckCircle, 
  XCircle, 
  LogIn, 
  LogOut, 
  FileText, 
  Send, 
  AlertTriangle,
  Shield,
  Camera,
  Mail,
  Phone,
  Lock
} from 'lucide-react';
import { GuestHistoryEntry } from '../../types/guest';
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';

interface HistoryTabProps {
  history: GuestHistoryEntry[];
  loading?: boolean;
}

/**
 * HistoryTab - Composant pour afficher l'historique chronologique des événements
 * Requirement 2.6: Timeline avec dates, actions et utilisateurs
 */
const HistoryTab: React.FC<HistoryTabProps> = ({ history, loading = false }) => {
  
  // Get icon for action type
  const getActionIcon = (action: string) => {
    const actionLower = action.toLowerCase();
    
    if (actionLower.includes('check-in') || actionLower.includes('checkin') || actionLower.includes('arrivée')) {
      return { icon: LogIn, color: 'text-green-500', bg: 'bg-green-100' };
    }
    if (actionLower.includes('check-out') || actionLower.includes('checkout') || actionLower.includes('départ')) {
      return { icon: LogOut, color: 'text-blue-500', bg: 'bg-blue-100' };
    }
    if (actionLower.includes('création') || actionLower.includes('créé') || actionLower.includes('create')) {
      return { icon: FileText, color: 'text-purple-500', bg: 'bg-purple-100' };
    }
    if (actionLower.includes('modification') || actionLower.includes('modifié') || actionLower.includes('update') || actionLower.includes('edit')) {
      return { icon: Edit, color: 'text-orange-500', bg: 'bg-orange-100' };
    }
    if (actionLower.includes('validation') || actionLower.includes('validé') || actionLower.includes('validate')) {
      return { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100' };
    }
    if (actionLower.includes('clôture') || actionLower.includes('clôturé') || actionLower.includes('close')) {
      return { icon: Lock, color: 'text-gray-500', bg: 'bg-gray-100' };
    }
    if (actionLower.includes('sync') || actionLower.includes('transmission') || actionLower.includes('envoi')) {
      return { icon: Send, color: 'text-blue-500', bg: 'bg-blue-100' };
    }
    if (actionLower.includes('anomalie') || actionLower.includes('alerte') || actionLower.includes('alert')) {
      return { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-100' };
    }
    if (actionLower.includes('résolu') || actionLower.includes('resolved')) {
      return { icon: Shield, color: 'text-green-500', bg: 'bg-green-100' };
    }
    if (actionLower.includes('photo') || actionLower.includes('image') || actionLower.includes('document')) {
      return { icon: Camera, color: 'text-indigo-500', bg: 'bg-indigo-100' };
    }
    if (actionLower.includes('email')) {
      return { icon: Mail, color: 'text-cyan-500', bg: 'bg-cyan-100' };
    }
    if (actionLower.includes('téléphone') || actionLower.includes('phone')) {
      return { icon: Phone, color: 'text-teal-500', bg: 'bg-teal-100' };
    }
    if (actionLower.includes('annulé') || actionLower.includes('supprimé') || actionLower.includes('cancel') || actionLower.includes('delete')) {
      return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100' };
    }
    
    // Default
    return { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100' };
  };

  // Format relative date
  const formatRelativeDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    
    if (isToday(date)) {
      return `Aujourd'hui à ${format(date, 'HH:mm', { locale: fr })}`;
    }
    if (isYesterday(date)) {
      return `Hier à ${format(date, 'HH:mm', { locale: fr })}`;
    }
    
    const distance = formatDistanceToNow(date, { addSuffix: true, locale: fr });
    return `${distance} (${format(date, 'dd/MM/yyyy à HH:mm', { locale: fr })})`;
  };

  // Sort history by date (most recent first)
  const historyArray = Array.isArray(history) ? history : [];
  const sortedHistory = [...historyArray].sort((a, b) =>
    new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime()
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Chargement de l'historique...</span>
      </div>
    );
  }

  // Empty state
  if (history.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun historique</h3>
        <p className="text-gray-600">Aucune action n'a été enregistrée pour cet invité.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Historique des modifications ({history.length} événement{history.length > 1 ? 's' : ''})
      </h3>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        <div className="space-y-4">
          {sortedHistory.map((entry, index) => {
            const { icon: ActionIcon, color, bg } = getActionIcon(entry.action);
            const isFirst = index === 0;

            return (
              <div key={entry.id} className="relative flex gap-4">
                {/* Icon circle */}
                <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full ${bg} flex items-center justify-center ${isFirst ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}>
                  <ActionIcon className={`w-5 h-5 ${color}`} />
                </div>

                {/* Content */}
                <div className={`flex-1 pb-4 ${index < sortedHistory.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="font-medium text-gray-900">{entry.action}</h4>
                      {isFirst && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                          Plus récent
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-3">{entry.description}</p>

                    {/* Meta info */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {entry.performedBy}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatRelativeDate(entry.performedAt)}
                      </span>
                    </div>

                    {/* Metadata details (expandable) */}
                    {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800 font-medium">
                          Voir les détails
                        </summary>
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs">
                          <table className="w-full">
                            <tbody>
                              {Object.entries(entry.metadata).map(([key, value]) => (
                                <tr key={key} className="border-b border-gray-200 last:border-0">
                                  <td className="py-1 pr-4 font-medium text-gray-700 whitespace-nowrap">
                                    {key}
                                  </td>
                                  <td className="py-1 text-gray-600 break-all">
                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HistoryTab;
